<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from "vue";
import { vIntersectionObserver } from "@vueuse/components";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import VDivider from "@magnit/core/src/components/VDivider/VDivider.vue";
import { collectCardDetailTabPayload } from "../utils/analytics";
import type { ICatalogBFFDetailedGood } from "../types/goods";
import { CatalogBFFDetailType } from "../types/goods";
import useProduct from "../composables/useProduct";
import type { AnalyticsEventArguments } from "../types/analytics";
import type { IPromoProduct } from "@magnit/unit-catalog/src/types";
import type { ITextProps } from "@magnit/core/src/components/VText/VText.types";

const props = defineProps<{
  product: ICatalogBFFDetailedGood | IPromoProduct;
  categoryName: string;
}>();

const emit = defineEmits<{
  analytics: AnalyticsEventArguments;
}>();

const { productDetailsParameters } = useProduct(props.product);

const activeTabRef = ref<string>(
  productDetailsParameters.value?.[0]?.name ?? "",
);

const ParametersList = defineAsyncComponent(
  () => import("./ParametersList.vue"),
);
const ParametersFlat = defineAsyncComponent(
  () => import("./ParametersFlat.vue"),
);

const getComponentByType = (type: CatalogBFFDetailType) => {
  switch (type) {
    case CatalogBFFDetailType.tableType:
      return ParametersList;
    case CatalogBFFDetailType.stringType:
    default:
      return ParametersFlat;
  }
};

const displayTabList = computed(() =>
  productDetailsParameters.value.map((s) => {
    const active = s.name === activeTabRef.value;
    const className = {
      "product-details-parameters__tab--active": active,
    };
    const color: ITextProps["color"] = active ? "primary" : "secondary";
    return { className, color, title: s.name };
  }),
);

const displayParamsList = computed(() => {
  return productDetailsParameters.value.map((s) => {
    const { name, parameters = [] } = s;
    const active = name === activeTabRef.value;
    const hide = { ml: false, l: !active };
    const component = getComponentByType(s.type);
    return {
      hide,
      component,
      name,
      parameters,
    };
  });
});

const handleTabClick = (title: string) => {
  activeTabRef.value = title;
};

const onVisibility = (
  [{ isIntersecting }]: IntersectionObserverEntry[],
  name: string,
) => {
  if (!isIntersecting) return;
  emit(
    "analytics",
    "CardDetail:Tab:View",
    collectCardDetailTabPayload(name, props.product, props.categoryName),
  );
};

watch(productDetailsParameters, (next) => {
  const groupNames = (next ?? []).map((s) => s.name);
  if (groupNames.includes(activeTabRef.value) || !groupNames.length) return;
  activeTabRef.value = groupNames[0];
});
</script>

<template>
  <section class="product-details-parameters">
    <VHidden :hide="{ xs: true, l: false }">
      <div
        v-if="displayTabList.length"
        class="product-details-parameters__tabs"
        data-test-id="product-bookmarks"
      >
        <VText
          v-for="tab in displayTabList"
          :key="tab.title"
          class="product-details-parameters__tab"
          :class="tab.className"
          :font="{ xs: 'title-medium', l: 'body-large-accent' }"
          :color="tab.color"
          @click="handleTabClick(tab.title)"
        >
          {{ tab.title }}
        </VText>
      </div>
    </VHidden>
    <div
      v-for="(single, index) in displayParamsList"
      :key="single.name"
      class="product-details-parameters__item"
    >
      <VHidden
        v-if="index === 0"
        class="product-details-parameters__divider product-details-parameters__divider_first"
        :hide="{ xs: false, l: true }"
      >
        <VDivider :thickness="4" />
      </VHidden>
      <VHidden
        :hide="{ xs: false, l: true }"
        class="product-details-parameters__item-title"
      >
        <VText tag="div" :font="{ xs: 'title-medium', l: 'body-large-accent' }">
          {{ single.name }}
        </VText>
      </VHidden>
      <VHidden
        :hide="single.hide"
        class="product-details-parameters__item-content"
      >
        <component
          :is="single.component"
          v-intersection-observer="
            (arg: IntersectionObserverEntry[]) => onVisibility(arg, single.name)
          "
          :parameters="single.parameters"
        />
      </VHidden>
      <VHidden
        class="product-details-parameters__divider"
        :hide="{ xs: false, l: true }"
      >
        <VDivider :thickness="4" />
      </VHidden>
    </div>
  </section>
</template>

<style lang="postcss" scoped>
.product-details-parameters {
  &__tabs {
    margin-bottom: var(--pl-unit-x5);
    border-bottom: 1px solid var(--pl-divider-secondary);
    display: flex;
    gap: var(--pl-unit-x6);
    overflow: auto hidden;

    /* пока-что скрываю скроллбар, т.к. он сильно ломает визуальный стиль компонента */
    scrollbar-width: none;

    /* эх надо не забывать этот хак специально для сафари при скрытии скроллбаров, вообще миксин какой-то скрутить бы чтоли? */
    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__tab {
    padding-bottom: var(--pl-unit-x3);
    cursor: pointer;
    text-wrap: nowrap;
    white-space: nowrap;

    &--active {
      position: relative;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: -1px;
        height: 2px;
        width: 100%;
        background: var(--pl-icon-primary);
      }
    }
  }

  &__item {
    & + & {
      margin-top: var(--pl-unit-x4);
    }

    &-title {
      margin-bottom: var(--pl-unit-x2);
    }
  }

  &__divider {
    margin: var(--pl-unit-x4) calc(-1 * var(--pl-wrapper-indent))
      var(--pl-unit-x5) calc(-1 * var(--pl-wrapper-indent));
  }
}
</style>
